import { FilterSelectAutocomplete } from '@hub/common/inputs/FilterSelectAutocomplete';
import type { ButtonGroupDefinition } from '@hub/common/ButtonGroup/ButtonGroup';
import { ButtonGroup } from '@hub/common/ButtonGroup/ButtonGroup';
import { Icon, IconColor, IconID } from '@hub/common/Icon/Icon';
import { useTranslation } from 'next-i18next';
import type { ChangeEventHandler } from 'react';
import React, { useState } from 'react';
import type { LifetimeMaterialMultipleLinearParams, SpatialTypeOfMovement } from '@igus/tribology-domain';
import { SpatialTypeOfMovementDual, SpatialTypeOfMovementSingle } from '@igus/tribology-domain';
import type {
  LifetimeConfigTypeOfOperation,
  OppositeMaterialKeyType,
} from '@hub/free-form-hub/common/lifetime/lifeTime.types';
import { lifetimeConfigTypeOfOperation, OppositeMaterialMap } from '@hub/free-form-hub/common/lifetime/lifeTime.types';
import { Button, TYPE } from '@hub/common/Button/Button';
import { useRouter } from 'next/router';
import { Formik } from 'formik';
import classNames from 'classnames';
import styles from '@hub/common/BasicNumberInput/BasicNumberInput.module.scss';
import type { LifetimeLoadByForceAndArea } from '@igus/tribology-domain/src/lib/lifetime/lifetime.definitions';
import {
  AreaCode,
  DurationCode,
  ForceCode,
  getUnitLabelForUnitSystem,
  LinearFrequencyCode,
  TemperatureCode,
} from '@icommerce/units';
import type { CurrentLifeTimeConfiguration } from '@hub/free-form-hub/common/lifetime/useLifetime';
import { fromMetricAreaMilli, fromMetricTemperature, UnitSystem } from '@igus/kopla-domain';
import { Modal, ModalVariant } from '@hub/common/Modal/Modal';
import { getIcommerceUnitSystemName, getLengthCode } from '@hub/bearing-hub/common/useUnitSystem/useUnitSystem';

export type LifetimeParameterFormProps = {
  currentLifeTimeConfiguration: CurrentLifeTimeConfiguration;
  onCalculate: (
    lifetimeParameters: LifetimeMaterialMultipleLinearParams,
    typeOfMovement: SpatialTypeOfMovement,
    typeOfOperation: LifetimeConfigTypeOfOperation
  ) => void;
  unitSystem: UnitSystem;
  isEmbedded?: boolean;
};

export const LifetimeParametersForm = ({
  currentLifeTimeConfiguration,
  onCalculate,
  unitSystem,
  isEmbedded,
}: LifetimeParameterFormProps) => {
  const { t } = useTranslation(['common']);
  const router = useRouter();
  const { locale } = router;
  const [expanded, setExpanded] = useState(isEmbedded || false);
  const [hintMessage, setHintMessage] = useState<string>(undefined);
  const [typeOfMovement, setTypeOfMovements] = useState<SpatialTypeOfMovement>(SpatialTypeOfMovementSingle.linear);
  const [typeOfOperation, setTypeOfOperation] = useState<LifetimeConfigTypeOfOperation>(
    lifetimeConfigTypeOfOperation.continous
  );
  const temperatureText = unitSystem === UnitSystem.imperial ? '73.4 °F' : '23 °C';

  const toggleButton = (
    <button
      data-testid="toggle-life-time-form"
      className="text-igus-primary"
      type="button"
      onClick={() => {
        setExpanded(!expanded);
        window.document.body.classList.remove('overflow-hidden');
      }}
    >
      <span className="mr-2">{t(`LIFETIME_PARAMETERS_LINK`, { ns: 'free-form-hub/lifeTime' })}</span>
      <div className="-rotate-90 inline-block">
        <Icon size={10} color={IconColor.ORANGE} id={expanded ? IconID.CHEVRON_RIGHT : IconID.CHEVRON_LEFT} />
      </div>
    </button>
  );

  const formButtonGroup = (onSubmit: () => void, onReset: () => void) => (
    <div className="flex justify-end items-center w-full">
      <div className="w-[240px] mr-6">
        <Button
          text={t('RESTORE_DEFAULT_VALUES', { ns: 'free-form-hub/lifeTime' })}
          testId="restore_default_values_button"
          type={TYPE.TERTIARY}
          onClick={onReset}
        />
      </div>
      <div className="w-[240px] mr-6">
        <Button
          icon={<Icon size={10} color={IconColor.WHITE} id={IconID.LIFETIME_CALCULATOR} />}
          text={t('CALCULATE', { ns: 'free-form-hub/lifeTime' })}
          type={TYPE.PRIMARY}
          onClick={onSubmit}
          testId="calculate_lifetime_button"
        />
      </div>
    </div>
  );

  const readOnlyItem = (label: string, attribute: string, value: string, unit: string, hint?: string) => (
    <div data-testid={attribute} className="flex flex-col relative mr-6 lg:w-[150px] w-full">
      <span className="block mb-2 text-sm ">
        {label}
        {hint && (
          <span
            className="ml-2 cursor-pointer"
            data-testid="show-life-time-modal-hint"
            onClick={event => {
              event.stopPropagation();
              setHintMessage(hint);
            }}
          >
            <Icon size={6} id={IconID.INFO_CIRCLE_GRAY} />
          </span>
        )}
      </span>
      <div className="font-bold min-h-[40px] text-[#666666] ">
        {value}
        <span>{unit}</span>
      </div>
    </div>
  );

  const inputNumber = (
    label: string,
    values: string | number,
    name: string,
    unit: string,
    handleChange: ChangeEventHandler<HTMLInputElement>
  ) => (
    <div className="relative mr-6 lg:w-60 w-full">
      <label htmlFor={name} className="block mb-2 text-sm">
        {label}
      </label>
      <div className="relative">
        <span className={classNames('right-4', 'absolute', 'w-12', 'mt-2', 'text-right')}>{unit}</span>
        <input
          className={classNames(
            'w-full',
            'mb-2',
            'mr-4',
            'px-6',
            'py-2',
            'appearance-none',
            'focus:outline-none',
            'border',
            'border-gray-200',
            'rounded',
            styles.input
          )}
          type="number"
          onChange={handleChange}
          value={values}
          id={name}
          name={name}
        />
      </div>
    </div>
  );

  const buttonGroupItem = (
    buttonGroupDefinition: ButtonGroupDefinition<
      LifetimeConfigTypeOfOperation | SpatialTypeOfMovementSingle | SpatialTypeOfMovementDual
    >[],
    selectedButton: number,
    onSectionTabClick: (value: string) => void
  ) => (
    <div className="mb-6 w-full">
      <ButtonGroup<string>
        buttonGroupDefinition={buttonGroupDefinition}
        selectedButton={selectedButton}
        onButtonClick={onSectionTabClick}
      />
    </div>
  );

  const hintModal = (
    <Modal
      variant={ModalVariant.ALERT}
      visible={hintMessage !== undefined}
      onClose={() => setHintMessage(undefined)}
      headline={t(`HEADLINES.LIFETIME`, { ns: 'free-form-hub/freeFormHubDetails' })}
    >
      <ul data-testid="life-time-modal-content">{hintMessage}</ul>
    </Modal>
  );

  const form = (
    <Formik<LifetimeMaterialMultipleLinearParams>
      initialValues={structuredClone(currentLifeTimeConfiguration)}
      onSubmit={parameters => onCalculate(parameters, typeOfMovement, typeOfOperation)}
    >
      {({ values, handleChange, handleSubmit, handleReset, setFieldValue }) => (
        <form onSubmit={handleSubmit}>
          {/* <--- Section Dimensions ---> */}
          <div className="flex flex-col mb-12">
            <div className="font-bold mb-6">{t('SECTION_DIMENSIONS', { ns: 'free-form-hub/lifeTime' })}</div>
            <div className="flex flex-row w-full items-start lg:items-end lg:flex-wrap justify-between lg:justify-start flex-wrap-reverse">
              {inputNumber(
                t('SURFACE_AREA', { ns: 'free-form-hub/lifeTime' }),
                fromMetricAreaMilli(unitSystem, (values.load as LifetimeLoadByForceAndArea).area).toLocaleString(
                  locale
                ),
                'load.area',
                getUnitLabelForUnitSystem(AreaCode.CUBIC_MILLIMETER, getIcommerceUnitSystemName(unitSystem)),
                handleChange
              )}
              {inputNumber(
                t('LOAD', { ns: 'free-form-hub/lifeTime' }),
                (values.load as LifetimeLoadByForceAndArea).force,
                'load.force',
                getUnitLabelForUnitSystem(ForceCode.NEWTON, getIcommerceUnitSystemName(unitSystem)),
                value => handleChange(value)
              )}
            </div>
          </div>

          {/* <--- Section Environment ---> */}
          <div className="flex flex-col mb-12">
            <div className="font-bold mb-6">{t('SECTION_ENVIRONMENT', { ns: 'free-form-hub/lifeTime' })}</div>
            <div className="flex flex-row w-full items-start lg:items-end lg:flex-wrap justify-between lg:justify-start flex-wrap-reverse">
              {readOnlyItem(
                t('TEMPERATURE', { ns: 'free-form-hub/lifeTime' }),
                'temperature',
                fromMetricTemperature(unitSystem, values.temperature).toLocaleString(locale),
                getUnitLabelForUnitSystem(TemperatureCode.CELSIUS, getIcommerceUnitSystemName(unitSystem)),
                t('TEMPERATURE_HINT', { ns: 'free-form-hub/lifeTime', temperature: temperatureText })
              )}
              {readOnlyItem(
                t('ENVIRONMENT', { ns: 'free-form-hub/lifeTime' }),
                'enviroment',
                values.environment.toString(),
                '',
                t('ENVIRONMENT_HINT', { ns: 'free-form-hub/lifeTime', temperature: temperatureText })
              )}
              {inputNumber(
                t('ACCEPTABLE_WEAR', { ns: 'free-form-hub/lifeTime' }),
                values.acceptableWear,
                'acceptableWear',
                getLengthCode(unitSystem),
                handleChange
              )}
            </div>
          </div>

          {/* <--- Section Materials ---> */}
          <div className="flex flex-col mb-12">
            <div className="font-bold mb-6">{t('SECTION_MATERIALS', { ns: 'free-form-hub/lifeTime' })}</div>
            <div className="flex flex-row w-full items-start lg:items-end lg:flex-wrap justify-between lg:justify-start flex-wrap-reverse">
              <div className="relative mr-6 lg:w-60 w-64" data-testid="oppositeMaterialId">
                <label htmlFor="select_filter_oppositeMaterialId" className="block mb-2 text-sm">
                  {t('COUNTER_PARTNER', { ns: 'free-form-hub/lifeTime' })}
                </label>
                <FilterSelectAutocomplete
                  isClearable={false}
                  id="select_filter_oppositeMaterialId"
                  placeholder={t('COUNTER_PARTNER', { ns: 'free-form-hub/lifeTime' })}
                  hideOptionsModel
                  options={Object.keys(OppositeMaterialMap).map(key => ({
                    label: t(`SHAFT_MATERIAL.${OppositeMaterialMap[key as OppositeMaterialKeyType]}`, {
                      ns: 'free-form-hub/lifeTime',
                    }),
                    value: key,
                  }))}
                  value={{
                    label: t(
                      `SHAFT_MATERIAL.${OppositeMaterialMap[values.oppositeMaterialId as OppositeMaterialKeyType]}`,
                      {
                        ns: 'free-form-hub/lifeTime',
                      }
                    ),
                    value: values.oppositeMaterialId,
                  }}
                  onChange={value => setFieldValue('oppositeMaterialId', value)}
                />
              </div>
            </div>
          </div>

          {/* <--- Section Type of Operation ---> */}
          <div className="flex flex-col mb-12">
            <div className="font-bold mb-6">{t('SECTION_TYPE_OF_OPERATION', { ns: 'free-form-hub/lifeTime' })}</div>
            {buttonGroupItem(
              [
                {
                  text: t('CONTINUOUS', { ns: 'free-form-hub/lifeTime' }),
                  value: lifetimeConfigTypeOfOperation.continous,
                },
                {
                  text: t('INTERMITTENT', { ns: 'free-form-hub/lifeTime' }),
                  value: lifetimeConfigTypeOfOperation.intermittent,
                },
              ],
              Object.values(lifetimeConfigTypeOfOperation).findIndex(value => value === typeOfOperation),
              value => {
                if (value === lifetimeConfigTypeOfOperation.intermittent) {
                  void setFieldValue('intermittentParams', { onTime: 0, offTime: 0 });
                }
                void setFieldValue('intermittentParams', undefined);
                setTypeOfOperation(value as LifetimeConfigTypeOfOperation);
              }
            )}
            <div className="flex lg:flex-row flex-col items-start lg:items-end justify-between lg:justify-start  mb-12">
              {inputNumber(
                t('ACTIVITY_MINUTES_PER_HOUR', { ns: 'free-form-hub/lifeTime' }),
                values.activityMinutesPerHour,
                'activityMinutesPerHour',
                getUnitLabelForUnitSystem(DurationCode.MINUTE, getIcommerceUnitSystemName(unitSystem)),
                value => handleChange(value)
              )}

              {typeOfOperation === lifetimeConfigTypeOfOperation.intermittent && (
                <>
                  {inputNumber(
                    t('ON_TIME', { ns: 'free-form-hub/lifeTime' }),
                    values.intermittentParams?.onTime,
                    'intermittentParams.onTime',
                    getUnitLabelForUnitSystem(DurationCode.MINUTE, getIcommerceUnitSystemName(unitSystem)),
                    value => handleChange(value)
                  )}
                  {inputNumber(
                    t('OFF_TIME', { ns: 'free-form-hub/lifeTime' }),
                    values.intermittentParams?.offTime,
                    'intermittentParams.offTime',
                    getUnitLabelForUnitSystem(DurationCode.MINUTE, getIcommerceUnitSystemName(unitSystem)),
                    value => handleChange(value)
                  )}
                </>
              )}
            </div>
          </div>

          {/* <--- Section Type of Motion ---> */}
          <div className="flex flex-col mb-12">
            <div className="font-bold mb-6">{t('SECTION_TYPE_OF_MOTION', { ns: 'free-form-hub/lifeTime' })}</div>
            {buttonGroupItem(
              [
                {
                  text: t('ROTATION', { ns: 'free-form-hub/lifeTime' }),
                  value: SpatialTypeOfMovementSingle.rotation,
                  disabled: true,
                },
                {
                  text: t('LINEAR', { ns: 'free-form-hub/lifeTime' }),
                  value: SpatialTypeOfMovementSingle.linear,
                },
                {
                  text: t('PIVOTING', { ns: 'free-form-hub/lifeTime' }),
                  value: SpatialTypeOfMovementSingle.pivoting,
                  disabled: true,
                },
                {
                  text: t('LINEAR_AND_ROTATION', { ns: 'free-form-hub/lifeTime' }),
                  value: SpatialTypeOfMovementDual.linearAndRotation,
                  disabled: true,
                },
                {
                  text: t('LINEAR_AND_PIVOTING', { ns: 'free-form-hub/lifeTime' }),
                  value: SpatialTypeOfMovementDual.linearAndPivoting,
                  disabled: true,
                },
              ],
              Object.values({ ...SpatialTypeOfMovementSingle, ...SpatialTypeOfMovementDual }).findIndex(
                value => value === typeOfMovement
              ),
              value => setTypeOfMovements(value as SpatialTypeOfMovement)
            )}
            <div className="flex flex-row w-full items-start lg:items-end lg:flex-wrap justify-between lg:justify-start flex-wrap-reverse">
              {inputNumber(
                t('LINEAR_STROKE_LENGTH', { ns: 'free-form-hub/lifeTime' }),
                values.linearStrokeLength,
                'linearStrokeLength',
                getLengthCode(unitSystem),
                handleChange
              )}
              {inputNumber(
                t('LINEAR_FREQUENCY', { ns: 'free-form-hub/lifeTime' }),
                values.linearFrequency,
                'linearFrequency',
                getUnitLabelForUnitSystem(
                  LinearFrequencyCode.REVOLUTION_PER_MINUTE,
                  getIcommerceUnitSystemName(unitSystem)
                ),
                handleChange
              )}
            </div>
          </div>
          {formButtonGroup(handleSubmit, handleReset)}
        </form>
      )}
    </Formik>
  );

  return (
    <div data-testid="life-time-calculator" className={isEmbedded ? 'mt-6' : 'mt-4'}>
      {hintModal}
      {!isEmbedded && toggleButton}
      {expanded && (
        <div
          className={`ml-6 overflow-hidden transition-max-height duration-250 ease-in-out ${
            expanded ? 'max-h-[100%]' : 'max-h-[0px]'
          }`}
        >
          {form}
        </div>
      )}
    </div>
  );
};
